import { useSFDCAuthState } from "@client-shared/providers/auth";
import useVendorContext, { setInitialized, setVendor } from "@client-shared/providers/vendor";
import type { Vendor } from "@shared/types";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { setAuthCallbackTestId } from "./AuthCallback.utils";

export const AuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const code = React.useMemo(() => searchParams.get("code"), [searchParams]);
  const [authState, , authUtils] = useSFDCAuthState();
  const [vendorState, vendorDispatch] = useVendorContext();
  const { t } = useTranslation();
  const isGettingReady = React.useRef(false);

  const handleSetVendorAndInitializeVendorProvider = React.useCallback(
    (vendor: Vendor | undefined) => {
      vendorDispatch(setVendor(vendor));
      vendorDispatch(setInitialized(true));
    },
    [vendorDispatch]
  );

  React.useEffect(() => {
    if (!code) return;
    const setTokensAndVendor = async () => {
      if (isGettingReady.current) return;
      isGettingReady.current = true;
      await authUtils?.fetchTokensOnAuth(code, handleSetVendorAndInitializeVendorProvider);
    };
    if (authState.loading) return;

    void setTokensAndVendor();
  }, [authUtils, code, handleSetVendorAndInitializeVendorProvider, authState.loading, vendorDispatch]);

  const hasError = React.useMemo(() => {
    if (!code) return true;
    if (authState.loading) return false;
    if (
      (!authState.loading && authState.initialized && !authState.sfdcAuthToken && !authState.sfdcIdToken && !authState.sfdcRefreshToken) ||
      (!authState.loading && authState.initialized && !authState.isRoasterAdmin && !vendorState.currentVendor)
    ) {
      return true;
    }
    return false;
  }, [authState, code, vendorState]);

  const isCallbackLoading = React.useMemo(() => !authState.initialized && authState.loading, [authState.initialized, authState.loading]);

  if (hasError) {
    return (
      <div style={{ textAlign: "center", marginTop: "50px" }}>
        <h1 data-testid={setAuthCallbackTestId("error-title")}>{t("components.AuthCallback.error")}</h1>
        <p data-testid={setAuthCallbackTestId("error-message")}>{t("components.AuthCallback.errorMessage")}</p>
      </div>
    );
  }

  return (
    <div style={{ textAlign: "center", marginTop: "50px" }}>
      {isCallbackLoading ? (
        <>
          <h1 data-testid={setAuthCallbackTestId("loading-title")}>{t("components.AuthCallback.loggingIn")}</h1>
          <p data-testid={setAuthCallbackTestId("loading-message")}>{t("components.AuthCallback.pleaseWait")}</p>
        </>
      ) : (
        <>
          <h1 data-testid={setAuthCallbackTestId("title")}>{t("components.AuthCallback.title")}</h1>
          <p data-testid={setAuthCallbackTestId("message")}>{t("components.AuthCallback.redirecting")}</p>
        </>
      )}
    </div>
  );
};

export default AuthCallback;
